import { useCallback, useEffect, useState } from 'react';
import changelogService from '../../../services/compliance/changelog.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import {
  ComplianceChangelogCreate,
  ComplianceChangelogEntry,
} from '../../../types/compliance';
import Notification, { NotificationContainer } from '../../../components/Notification';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';


export default function ComplianceChangelog() {
  const { isManager: canManage } = useComplianceAccess();

  const [entries, setEntries] = useState<ComplianceChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<{ title: string; message: string } | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [version, setVersion] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [publishedAt, setPublishedAt] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await changelogService.list();
      setEntries(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Failed to load changelog', message: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setVersion('');
    setTitle('');
    setBody('');
    setPublishedAt('');
  };

  const startEdit = (entry: ComplianceChangelogEntry) => {
    setEditingId(entry.id);
    setVersion(entry.version);
    setTitle(entry.title);
    setBody(entry.body);
    // datetime-local expects YYYY-MM-DDTHH:MM — trim seconds + tz
    setPublishedAt(entry.published_at.slice(0, 16));
    setFormOpen(true);
  };

  const submit = useCallback(async () => {
    if (!version.trim() || !title.trim() || !body.trim()) {
      setErr({
        title: 'Missing fields',
        message: 'Version, title, and body are required.',
      });
      return;
    }
    const payload: ComplianceChangelogCreate = {
      version: version.trim(),
      title: title.trim(),
      body,
      published_at: publishedAt
        ? new Date(publishedAt).toISOString()
        : undefined,
    };
    setSaving(true);
    try {
      if (editingId) {
        const updated = await changelogService.update(editingId, payload);
        setEntries((prev) =>
          prev.map((e) => (e.id === editingId ? updated : e)),
        );
      } else {
        const created = await changelogService.create(payload);
        setEntries((prev) => [created, ...prev]);
      }
      setFormOpen(false);
      resetForm();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Failed to save entry', message: msg });
    } finally {
      setSaving(false);
    }
  }, [editingId, version, title, body, publishedAt]);

  const remove = useCallback(async (entry: ComplianceChangelogEntry) => {
    if (!window.confirm(`Delete "${entry.title}" (${entry.version})?`)) return;
    try {
      await changelogService.remove(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Failed to delete entry', message: msg });
    }
  }, []);

  return (
    <div className="h-full w-full p-6 pb-24">
      <NotificationContainer>
        {err && (
          <Notification
            type="error"
            title={err.title}
            message={err.message}
            onClose={() => setErr(null)}
          />
        )}
      </NotificationContainer>

      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Release Notes</h1>
          <p className="text-sm text-muted">
            Compliance portal changelog — features, fixes, and policy updates.
          </p>
        </div>
        {canManage && !formOpen && (
          <button
            type="button"
            onClick={() => { resetForm(); setFormOpen(true); }}
            className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            + Compose Entry
          </button>
        )}
      </div>

      {formOpen && (
        <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1 text-label">Version</span>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="v1.4.0"
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-label">Published at</span>
              <input
                type="datetime-local"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              />
              <span className="block text-[0.6875rem] text-gray-500 mt-0.5">
                Leave blank to publish now.
              </span>
            </label>
          </div>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Incidents subsystem shipped"
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </label>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Body</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Describe the release — features, fixes, migrations, anything user-facing…"
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm"
            />
          </label>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setFormOpen(false); resetForm(); }}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingId ? 'Update Entry' : 'Publish Entry'}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {!loading && entries.length === 0 && (
        <div className="text-sm text-muted">
          No entries yet.
        </div>
      )}

      <ol className="space-y-4">
        {entries.map((e) => (
          <li
            key={e.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs text-muted">
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {e.version}
                  </span>
                  <span>·</span>
                  <span>{new Date(e.published_at).toLocaleString()}</span>
                </div>
                <h2 className="mt-1 text-lg font-semibold text-body">
                  {e.title}
                </h2>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                  {e.body}
                </p>
              </div>
              {canManage && (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(e)}
                    className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(e)}
                    className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
